import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { debounce } from 'lodash'

import { useQueryList } from '@percent/utility'
import { ActionModal, AcknowledgeModal, AsyncSelect, FormField, Modal, SelectOption } from '@percent/lemonade'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useMutation } from '@percent/admin-dashboard/common/hooks'

const DEBOUNCE_MS = 500
const SEWA_COUNTRY_CODE = 'IND'
const SEWA_ORG_PAGE_SIZE = 25

export type AddOrganisationModalProps = {
  onClose: () => void
  refresh: () => void
}

export function AddOrganisationModal({ onClose, refresh }: AddOrganisationModalProps) {
  const { t } = useTranslation()
  const { adminService, sewaOnboardingService } = useServices()

  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedOrgId, setSelectedOrgId] = useState('')

  const debouncedSetSearch = useRef(debounce((value: string) => setDebouncedSearch(value), DEBOUNCE_MS)).current

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch])

  const queryParams = useMemo(
    () => ({
      pageSize: SEWA_ORG_PAGE_SIZE,
      countryCode: SEWA_COUNTRY_CODE,
      ...(debouncedSearch ? { query: debouncedSearch } : {})
    }),
    [debouncedSearch]
  )

  const [{ data: organisationsData, isLoading: isOrgsLoading }] = useQueryList(
    ['v1/admin/organisations', debouncedSearch],
    adminService.getOrganisations,
    queryParams
  )

  const [{ isLoading, success, error }, { apiFunc, setError }] = useMutation(sewaOnboardingService.onboardOrganisation)

  const searchResults: SelectOption[] = useMemo(
    () => organisationsData?.map(org => ({ value: org.id, label: org.name })) ?? [],
    [organisationsData]
  )

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value)
    debouncedSetSearch(value)
  }

  const handleClose = () => {
    if (success) refresh()
    onClose()
  }

  const handleSubmit = async () => {
    await apiFunc({ organisationId: selectedOrgId })
  }

  return (
    <Modal open onClose={handleClose}>
      {success && (
        <AcknowledgeModal
          result="positive"
          title={t('sewaOnboarding.modal.add.successTitle')}
          buttonText={t('button.close')}
          handleClose={handleClose}
        >
          {t('sewaOnboarding.modal.add.successDescription')}
        </AcknowledgeModal>
      )}

      {!success && error && (
        <AcknowledgeModal
          result="negative"
          title={t('sewaOnboarding.modal.add.errorTitle')}
          buttonText={t('button.close')}
          handleClose={() => setError('')}
        >
          {t('sewaOnboarding.modal.add.errorDescription')}
        </AcknowledgeModal>
      )}

      {!success && !error && (
        <ActionModal
          title={t('sewaOnboarding.modal.add.title')}
          primaryButtonText={t('sewaOnboarding.modal.add.submit')}
          secondaryButtonText={t('button.cancel')}
          loading={isLoading}
          disabled={!selectedOrgId}
          handleSubmit={handleSubmit}
          handleClose={onClose}
        >
          <FormField label={t('sewaOnboarding.modal.add.organisationLabel')} necessity="required">
            <AsyncSelect
              options={searchResults}
              loading={isOrgsLoading}
              setQuery={handleSearchInputChange}
              query={searchInput}
              onChange={option => {
                setSelectedOrgId(option?.value ?? '')
              }}
              placeholder={t('sewaOnboarding.modal.add.organisationPlaceholder')}
            />
          </FormField>
        </ActionModal>
      )}
    </Modal>
  )
}
